// generation.js (updated to handle video banners with vidu model and robust preview)
// @ts-nocheck

// API configuration
import { CDN_URL, GENERATION_API_URL } from '../../js/config.js';
import { getCurrentLanguage, applyTranslations, resolveImageGenError, resolveCreateCommonText, getByPath } from '../translations.js';
import { initOAuthManager } from '../utils/oauthManager.js';
import { storage } from '../utils/storage.js';
import { initSearchDropdown } from './ui/searchDropdown.js';
import { $, on, off, addClass, removeClass, html, css, val, trigger, ready } from '../../core/utils/dom.js';
import { moveDefaultToFront, dedupeById } from '../../core/utils/index.js';
import { normalizeGenderForFilter } from '../utils/genderUtils.js';
import { normalizeStyle, getCharacterFormStyle, getNsfwEnabled } from '../utils/userPrefs.js';
import { createRetryableHttpError, isTransientHttpStatus, withRetry } from '../utils/error-handling.js';
import { DYNAMIC_CATEGORY_8_KEYWORDS, collectUnrecognizedPromptTokens, getBlockedUnrecognizedPromptMessage, loadDanbooruTags } from './danbooruTags.js';
import { isLinkedBraceToken } from './utils/standalonePromptHelpers.js';
import { getBlockedNsfwPromptMessage } from '../utils/nsfwTags.js';
import { withCharacterGenderTag } from './characterGenderTag.js';
import { fetchCache } from '../utils/fetchCache.js';
import { parseOutputKeyFromPendingUrl, waitForQueuedImage } from './waitForQueuedImage.js';
import { notifyVideoContainerStarting } from './waitForQueuedVideo.js';
import { adoptWaitEstimate, remainingWaitSeconds } from './waitEstimate.js';
import { getDefaultStandingActionId } from '../utils/spriteFallbacks.js';
import {
  createAiProgressController,
  SLOW_PREVIEW_FLAVOR_KEYS,
  resolveAiProgressText,
} from './aiProgressStatus.js';

// ─── Quota & slow-path constants ─────────────────────────────────────────────

const QUOTA_LS_KEY = 'user_gen_quota';
const PENDING_JOB_LS_KEY = 'user_gen_pending';
const RETRYABLE_IMAGE_STATUS_CODES = [408, 429, 502, 503, 504];

/** Map previewId → button IDs that should show countdown / remaining text */
const PREVIEW_BUTTON_MAP = {
  'ai-character-prompt-preview': ['ai-generate-character-btn', 'generate-preview-btn', 'generate-preview-slow-btn'],
  'banner-image-preview': ['generate-banner-btn'],
};

const CHARACTER_PREVIEW_ID = 'ai-character-prompt-preview';
const SLOW_PREVIEW_PARK_ID = 'character-slow-preview-park';

// ─── Quota helpers ────────────────────────────────────────────────────────────

export const getStoredQuota = () => {
  try {
    const raw = localStorage.getItem(QUOTA_LS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const updateStoredQuota = (quota) => {
  if (!quota) return;
  try { localStorage.setItem(QUOTA_LS_KEY, JSON.stringify(quota)); } catch {}
  document.dispatchEvent(new CustomEvent('genQuotaUpdated', { detail: quota }));
};

const PREVIEW_QUOTA_ERRORS = new Set(['quota_exhausted', 'fast_quota_exhausted', 'slow_quota_exhausted']);

const readPreviewPageText = (type, key, fallback) => {
  const lang = getCurrentLanguage?.() || 'en';
  const section = type === 'banner' || type === 'background' ? 'create-scenario' : 'create-character';
  const path = section === 'create-scenario' ? `steps.banner.${key}` : key;
  const bundles = [window.translations?.[lang]?.[section], window.translations?.en?.[section]].filter(Boolean);
  for (const bundle of bundles) {
    const raw = getByPath(bundle, path);
    if (raw) return String(raw);
  }
  return fallback;
};

export const applyCreditsRemaining = (remaining) => {
  const credits = Number(remaining);
  if (!Number.isFinite(credits)) return;
  const currentUserData = storage.get('userData') || {};
  const updatedUserData = { ...currentUserData, sprite_credits: credits };
  storage.set('userData', updatedUserData);
  document.dispatchEvent(new CustomEvent('spriteCreditsUpdated', {
    detail: {
      sprite_credits: credits,
      current_tier: updatedUserData.current_tier,
    },
  }));
};

const getAuthToken = async () => {
  try {
    const oauthManager = initOAuthManager();
    return await oauthManager.getToken();
  } catch { return null; }
};

const createGenerationRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `gen-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const handleRetryTelemetry = (operationLabel, payload) => {
  if (!payload || typeof payload !== 'object') return;
  const hasRequiredFields = payload.endpoint && payload.method && Number.isFinite(payload.attempt);
  if (!hasRequiredFields) {
    console.warn(`[ImageGen] Retry telemetry missing required fields for ${operationLabel}:`, payload);
    return;
  }

  if (payload.event_type === 'request_retry_attempt') {
    console.warn(`[ImageGen] Retry attempt for ${operationLabel}:`, payload);
    return;
  }

  if (payload.event_type === 'request_retry_exhausted') {
    console.error(`[ImageGen] Retry exhausted for ${operationLabel}:`, payload);
  }
};

// ─── Generation session & slow-path state ────────────────────────────────────

/** Holds the abort callback for any active poll, keyed by previewId. */
const activePollAbort = new Map();

/** Per-preview generation token; stale in-flight results are discarded. */
const generationTokenByPreview = new Map();

/** Last slow-path job per preview: { fingerprint, pendingUrl } */
const slowPathStateByPreview = new Map();

const bumpGenerationToken = (previewId) => {
  const next = (generationTokenByPreview.get(previewId) ?? 0) + 1;
  generationTokenByPreview.set(previewId, next);
  activePollAbort.get(previewId)?.();
  return next;
};

const isGenerationStale = (previewId, token) => generationTokenByPreview.get(previewId) !== token;

/** Which character session the modal is showing. Banner previews stay a single session. */
let activeCharacterPreviewMode = null;
let characterPreviewRequestSerial = 0;

export const beginCharacterPreviewRequest = (previewMode) => {
  characterPreviewRequestSerial += 1;
  activeCharacterPreviewMode = normalizePreviewMode(previewMode);
  return characterPreviewRequestSerial;
};

export const isCurrentCharacterPreviewRequest = (serial) => (
  serial == null || serial === characterPreviewRequestSerial
);

/** Per-mode character preview: fingerprint, status, image, and slow job handles. */
const characterPreviewSessions = new Map();

const normalizePreviewMode = (previewMode) => (previewMode === 'slow' ? 'slow' : 'fast');

const previewSessionKey = (previewId, previewMode = 'fast') => {
  if (previewId !== CHARACTER_PREVIEW_ID) return previewId;
  return `${previewId}:${normalizePreviewMode(previewMode)}`;
};

const ensureSlowPreviewPark = () => {
  let park = document.getElementById(SLOW_PREVIEW_PARK_ID);
  if (park) return park;
  park = document.createElement('div');
  park.id = SLOW_PREVIEW_PARK_ID;
  park.hidden = true;
  park.setAttribute('aria-hidden', 'true');
  document.body.appendChild(park);
  return park;
};

const slowWaitNode = (previewId) => (
  document.getElementById(`slow-gen-countdown-${previewId}`)?.parentElement || null
);

const discardSlowWaitNode = (previewId = CHARACTER_PREVIEW_ID) => {
  slowWaitNode(previewId)?.remove();
};

/** Keep the live countdown node mounted so its timer survives a fast-preview swap. */
const parkSlowWaitIfPresent = () => {
  const preview = document.getElementById(CHARACTER_PREVIEW_ID);
  const node = slowWaitNode(CHARACTER_PREVIEW_ID);
  if (!preview || !node || !preview.contains(node)) return;
  ensureSlowPreviewPark().appendChild(node);
};

const mountSlowWait = () => {
  const preview = document.getElementById(CHARACTER_PREVIEW_ID);
  const node = slowWaitNode(CHARACTER_PREVIEW_ID);
  if (!preview || !node) return false;
  if (!preview.contains(node)) preview.replaceChildren(node);
  return true;
};

const slowWaitHost = (previewId) => {
  if (previewId !== CHARACTER_PREVIEW_ID || activeCharacterPreviewMode === 'slow') {
    return document.getElementById(previewId);
  }
  return ensureSlowPreviewPark();
};

const queueButtonIds = (previewId) => {
  if (previewId === CHARACTER_PREVIEW_ID) return ['generate-preview-slow-btn'];
  return PREVIEW_BUTTON_MAP[previewId] || [];
};

const restoreQueuedButtonLabels = (previewId) => {
  if (previewId !== CHARACTER_PREVIEW_ID) return;
  const storedQuota = getStoredQuota();
  if (!storedQuota) return;
  document.dispatchEvent(new CustomEvent('genQuotaUpdated', { detail: storedQuota }));
};

const isCharacterPollActive = (previewMode) => (
  activePollAbort.has(previewSessionKey(CHARACTER_PREVIEW_ID, previewMode))
);

const activateCharacterPreviewMode = (previewMode) => {
  activeCharacterPreviewMode = normalizePreviewMode(previewMode);
};

const rememberCharacterSession = (previewMode, patch) => {
  const mode = normalizePreviewMode(previewMode);
  const prev = characterPreviewSessions.get(mode) || {};
  characterPreviewSessions.set(mode, { ...prev, ...patch });
};

const discardCharacterSessionIfOwned = (previewMode, fingerprint) => {
  const session = characterPreviewSessions.get(normalizePreviewMode(previewMode));
  if (session?.fingerprint === fingerprint) {
    characterPreviewSessions.delete(normalizePreviewMode(previewMode));
  }
};

const invalidateOtherCharacterSession = (previewId, previewMode, fingerprint) => {
  if (previewId !== CHARACTER_PREVIEW_ID) return;
  const other = normalizePreviewMode(previewMode) === 'slow' ? 'fast' : 'slow';
  const otherSession = characterPreviewSessions.get(other);
  if (!otherSession || otherSession.fingerprint === fingerprint) return;
  bumpGenerationToken(previewSessionKey(previewId, other));
  characterPreviewSessions.delete(other);
  if (other === 'slow') slowPathStateByPreview.delete(previewId);
};

const buildSlowPromptFingerprint = ({
  prompt,
  type,
  style,
  gender,
  characterVisibility,
  bannerType,
  timeOfDay,
}) => JSON.stringify({
  prompt,
  type,
  style: normalizeStyle(style ?? 'anime'),
  gender: gender ?? null,
  characterVisibility: characterVisibility ?? 'public',
  bannerType: bannerType ?? 'image',
  timeOfDay: timeOfDay ?? null,
});

const resolveCharacterPreviewFingerprint = async () => {
  const promptEl = document.getElementById('ai-character-prompt');
  const outfitPromptEl = document.getElementById('ai-outfit-prompt');
  const genderEl = document.getElementById('ai-character-gender');
  const visibilityEl = document.getElementById('ai-character-visibility');
  const prompt = promptEl?.value?.trim() || '';
  if (!prompt) return null;

  const outfitPrompt = outfitPromptEl?.value?.trim() || '';
  const gender = genderEl?.value || null;
  const appearance = withCharacterGenderTag(prompt, gender);
  const combinedPrompt = outfitPrompt ? `${appearance}, ${outfitPrompt}` : appearance;
  const cleanedPrompt = await cleanCharacterPrompt(combinedPrompt);
  const characterVisibility = visibilityEl?.value === 'private' ? 'private' : 'public';
  const style = getCharacterFormStyle();

  return buildSlowPromptFingerprint({
    prompt: cleanedPrompt,
    type: 'character',
    style,
    gender,
    characterVisibility,
    bannerType: 'image',
    timeOfDay: null,
  });
};

const resolvePreviewFingerprint = async (previewId) => {
  if (previewId === 'ai-character-prompt-preview') return resolveCharacterPreviewFingerprint();
  return null;
};

// ─── Slow-path wait (job status, not R2 HEAD) ────────────────────────────────

const pollForQueuedImage = (pendingUrl, previewId, {
  promptFingerprint = null,
  outputKey = null,
  style = null,
  previewMode = 'slow',
} = {}) => new Promise((resolve, reject) => {
  const sessionKey = previewSessionKey(previewId, previewMode);
  activePollAbort.get(sessionKey)?.();

  const resolvedOutputKey = outputKey || parseOutputKeyFromPendingUrl(pendingUrl);
  const abortController = new AbortController();

  try {
    localStorage.setItem(PENDING_JOB_LS_KEY, JSON.stringify({
      pendingUrl,
      previewId,
      startedAt: Date.now(),
      promptFingerprint,
      outputKey: resolvedOutputKey,
      style,
    }));
  } catch {}

  const btnIds = queueButtonIds(previewId);
  const getBtns = () => btnIds.map(id => document.getElementById(id)).filter(Boolean);
  const estimatingLabel = resolveAiProgressText('generate_estimating')
    || resolveCreateCommonText('generate_estimating')
    || 'Estimating wait time…';

  const preview = slowWaitHost(previewId);
  let flavorController = null;
  if (preview) {
    preview.innerHTML = `
      <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:12px;color:var(--text-color);padding:12px;box-sizing:border-box;">
        <span id="slow-gen-countdown-${previewId}" style="font-size:2.2em;font-weight:bold;line-height:1;min-height:1.1em;">
          <i class="fas fa-spinner fa-spin" style="font-size:0.85em;" aria-hidden="true"></i>
        </span>
        <span id="slow-gen-eta-label-${previewId}" style="font-size:0.75em;opacity:0.7;text-align:center;">${estimatingLabel}</span>
        <p id="slow-gen-flavor-${previewId}" class="ai-progress-status" style="margin:0;font-size:0.8em;opacity:0.85;text-align:center;max-width:16em;line-height:1.35;" aria-live="polite">
          <span class="ai-progress-status__text"></span>
        </p>
      </div>`;
    const flavorEl = document.getElementById(`slow-gen-flavor-${previewId}`);
    flavorController = createAiProgressController(flavorEl);
    const characterName = document.getElementById('ai-character-name')?.value?.trim() || 'The character';
    flavorController.startFlavorRotation(SLOW_PREVIEW_FLAVOR_KEYS, {
      vars: { name: characterName },
    });
  }
  getBtns().forEach(btn => {
    btn.innerHTML = '<i class="fas fa-clock"></i> <span data-translate="generate_queued">In queue…</span>';
  });

  let aborted = false;
  let timerInterval = null;
  let waitEstimate = null;
  let waitStatus = null;
  let timerSwitched = false;

  const cleanup = ({ discardWait = true } = {}) => {
    aborted = true;
    abortController.abort();
    clearInterval(timerInterval);
    flavorController?.clear?.();
    activePollAbort.delete(sessionKey);
    if (discardWait) discardSlowWaitNode(previewId);
    restoreQueuedButtonLabels(previewId);
    try { localStorage.removeItem(PENDING_JOB_LS_KEY); } catch {}
  };

  activePollAbort.set(sessionKey, cleanup);

  const showPreparingLabel = () => {
    const etaLabel = document.getElementById(`slow-gen-eta-label-${previewId}`);
    if (etaLabel) {
      etaLabel.textContent = resolveCreateCommonText('generate_slow_polling')
        || 'Preparing your image…';
    }
  };

  const showWaitNote = () => {
    const etaLabel = document.getElementById(`slow-gen-eta-label-${previewId}`);
    if (!etaLabel || !waitEstimate) return;
    if (waitEstimate.stalled) etaLabel.textContent = 'Instance stalled, requeued';
  };

  const applyEstimate = (data) => {
    if (aborted || !data) return;
    const hadEstimate = Boolean(waitEstimate);
    const prevRevision = waitEstimate?.revision ?? null;
    waitEstimate = adoptWaitEstimate(waitEstimate, data);
    notifyVideoContainerStarting(data, resolvedOutputKey || pendingUrl || previewId, 'image');
    if (typeof data.status === 'string') waitStatus = data.status;
    if (!waitEstimate) return;
    if (!hadEstimate || waitEstimate.revision !== prevRevision) {
      timerSwitched = false;
      showPreparingLabel();
    }
    showWaitNote();
  };

  timerInterval = setInterval(() => {
    if (aborted) return;
    const remaining = remainingWaitSeconds(waitEstimate);
    const countdownEl = document.getElementById(`slow-gen-countdown-${previewId}`);
    const hasEstimate = Boolean(waitEstimate);

    if (waitEstimate?.stalled) {
      if (countdownEl) countdownEl.textContent = '';
      showWaitNote();
      return;
    }

    if (hasEstimate && remaining != null && remaining > 0) {
      if (countdownEl) countdownEl.textContent = `${remaining}s`;
      timerSwitched = false;
    } else if (hasEstimate && remaining === 0 && waitStatus === 'queued') {
      if (countdownEl) countdownEl.textContent = '0s';
      timerSwitched = false;
    } else if (hasEstimate && remaining === 0) {
      if (!timerSwitched) {
        timerSwitched = true;
        if (countdownEl) {
          countdownEl.replaceChildren();
          const loader = document.createElement('span');
          loader.className = 'slow-gen-overtime-loader';
          loader.setAttribute('role', 'status');
          loader.setAttribute('aria-live', 'polite');
          const spinner = document.createElement('span');
          spinner.className = 'slow-gen-overtime-loader-spinner';
          spinner.setAttribute('aria-hidden', 'true');
          loader.appendChild(spinner);
          countdownEl.appendChild(loader);
        }
        getBtns().forEach(btn => {
          btn.innerHTML = '<i class="fas fa-clock"></i> <span data-translate="generate_queued">In queue…</span>';
        });
      }
    }
  }, 1000);

  waitForQueuedImage({
    pendingUrl,
    outputKey: resolvedOutputKey,
    style,
    signal: abortController.signal,
    onEstimate: applyEstimate,
  }).then((imageUrl) => {
    if (aborted) return;
    cleanup({ discardWait: false });
    resolve(imageUrl);
  }).catch((error) => {
    if (aborted || error?.name === 'AbortError') return;
    cleanup();
    reject(error);
  });
});

export const resumePendingGeneration = async (previewId) => {
  try {
    const raw = localStorage.getItem(PENDING_JOB_LS_KEY);
    if (!raw) return;
    const {
      pendingUrl,
      previewId: storedId,
      startedAt,
      promptFingerprint,
      outputKey,
      style,
    } = JSON.parse(raw);
    if (storedId !== previewId) return;
    if (Date.now() - startedAt > 10 * 60 * 1000) {
      localStorage.removeItem(PENDING_JOB_LS_KEY);
      return;
    }
    if (promptFingerprint) {
      const currentFingerprint = await resolvePreviewFingerprint(previewId);
      if (!currentFingerprint || currentFingerprint !== promptFingerprint) {
        localStorage.removeItem(PENDING_JOB_LS_KEY);
        return;
      }
    }
    if (previewId === CHARACTER_PREVIEW_ID) {
      activateCharacterPreviewMode('slow');
      rememberCharacterSession('slow', {
        fingerprint: promptFingerprint || null,
        status: 'waiting',
        pendingUrl,
        outputKey: outputKey || null,
        imageUrl: null,
      });
    }
    const imageUrl = await pollForQueuedImage(pendingUrl, previewId, {
      promptFingerprint,
      outputKey,
      style,
      previewMode: 'slow',
    });
    commitPreviewResult(previewId, 'slow', imageUrl, false);
  } catch (e) {
    console.warn('[ImageGen] resumePendingGeneration failed:', e.message);
  }
};

async function isUserAuthenticated() {
  const oauthManager = initOAuthManager();
  const isAuthenticated = await oauthManager.isAuthenticated();
  return isAuthenticated;
}

const setPostLoginRedirect = (returnTo) => {
  try {
    if (!returnTo) return;
    // Session-scoped (tab) redirect target; consumed by callback.html after successful auth.
    sessionStorage.setItem('post_login_redirect', returnTo);
  } catch {
    // no-op
  }
};

const getCurrentPathForPostLoginRedirect = () => {
  try {
    const { pathname, search, hash } = window.location;
    if (!pathname) return null;
    // Don’t allow redirect loops into the auth callback.
    if (pathname.endsWith('/callback.html') || pathname === '/callback.html') return null;
    // Prefer absolute path for consistency with callback.html.
    return `${pathname}${search || ''}${hash || ''}`;
  } catch {
    return null;
  }
};

export async function showLoginRequired() {
  try {
    // For guests, prefer the free account modal (matches Hangout guest behavior).
    // Keep a fallback to the legacy login modal in case the free modal fails to load.
    if (!(await isUserAuthenticated())) {
      // Ensure successful login returns the user to the page that triggered auth (e.g., create-scenario).
      setPostLoginRedirect(getCurrentPathForPostLoginRedirect());
      try {
        const { showFreeAccountModal } = await import('../components/FreeAccountModal.js');
        if (showFreeAccountModal) {
          await showFreeAccountModal();
          return;
        }
      } catch (e) {
        console.warn('Failed to show free account modal; falling back to login modal', e);
      }
    }

    if (window.loginModalController?.show) {
      window.loginModalController.show();
      return;
    }

    // Fallback: import the controller and show modal
    const { default: loginModalController } = await import('../components/LoginModalController.js');
    if (loginModalController?.show) {
      loginModalController.show();
    }
  } catch (error) {
    console.error('Failed to show login modal:', error);
  }
}

// Cache for outfit translations
let outfitCache = {
  data: null,
  currentLang: null
};

// Cache for background images
const backgroundImageCache = new Map();

// Hardcoded translations for dropdown first items in 11 languages
const dropdownTranslations = {
  "en": { select_character: "Select a character", select_outfit: "Select an outfit" },
  "ja": { select_character: "キャラクターを選択", select_outfit: "衣装を選択" },
  "ko": { select_character: "캐릭터 선택", select_outfit: "의상 선택" },
  "es": { select_character: "Seleccionar un personaje", select_outfit: "Seleccionar un atuendo" },
  "fr": { select_character: "Sélectionner un personnage", select_outfit: "Sélectionner une tenue" },
  "pt-BR": { select_character: "Selecionar um personagem", select_outfit: "Selecionar uma roupa" },
  "ar": { select_character: "اختر شخصية", select_outfit: "اختر زيًا" },
  "id": { select_character: "Pilih karakter", select_outfit: "Pilih pakaian" },
  "zh-CN": { select_character: "选择一个角色", select_outfit: "选择一套服装" },
  "ru": { select_character: "Выбрать персонажа", select_outfit: "Выбрать наряд" },
  "hi": { select_character: "एक पात्र चुनें", select_outfit: "एक परिधान चुनें" }
};

const searchDropdownInstances = {};

const doesOutfitMatchGender = (outfit, gender) => {
  if (!gender || gender === 'all') return true;
  const outfitGender = normalizeGenderForFilter(outfit?.gender || outfit?.gender_text || outfit?.genderLabel);
  if (!outfitGender || outfitGender === 'all') return true;
  return outfitGender === gender;
};

const ensureDefaultOutfitFirst = (list, fallback) => moveDefaultToFront(list, fallback);

export const getBase64FromUrl = (url) => {
  if (!url) return Promise.resolve('');
  if (url.startsWith('data:')) return Promise.resolve(url);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = url;
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        console.warn('canvas base64 failed', e);
        resolve(url);
      }
    };
    img.onerror = () => resolve(url);
  });
};

// Helper function to show loading state
export function setLoadingState(elementId, isLoading) {
  const element = $(`#${elementId}`);
  if (!element) return;
  if (element.matches('button')) {
    const isHiddenCharacterBtn = elementId === 'ai-generate-character-btn';
    if (isLoading) {
      addClass(element, 'generating');
      element.setAttribute('aria-busy', 'true');
      if (!isHiddenCharacterBtn) element.disabled = true;
      html(element, '<i class="fas fa-spinner fa-spin"></i> Generating...');
      lockFormDuringImageGeneration(elementId, true);
    } else {
      removeClass(element, 'generating');
      element.removeAttribute('aria-busy');
      if (!isHiddenCharacterBtn) element.disabled = false;
      html(element, '<i class="fas fa-magic"></i> Generate');
      lockFormDuringImageGeneration(elementId, false);
      // Re-apply quota state so listeners can override the reset text
      const storedQuota = getStoredQuota();
      if (storedQuota) {
        document.dispatchEvent(new CustomEvent('genQuotaUpdated', { detail: storedQuota }));
      }
    }
  } else if (element.matches('select')) {
    element.disabled = isLoading;
  }
}

// Utility to lock/unlock specific elements during image generation
function lockFormDuringImageGeneration(buttonId, isLocking) {
  // Only lock the specific textarea/input related to the generation
  let targetSelectors = [];
  
  if (buttonId === 'ai-generate-character-btn') {
    targetSelectors = ['#ai-character-prompt']; // Only lock character prompt textarea
  } else if (buttonId === 'generate-banner-btn') {
    targetSelectors = ['#adventure-banner-prompt']; // Only lock banner prompt textarea
  } else if (buttonId === 'custom-bg-generate-btn') {
    targetSelectors = ['#custom-bg-textarea', '#custom-bg-name']; // Only lock custom bg fields
  } else {
    return; // Unknown button, don't lock anything
  }
  
  targetSelectors.forEach(selector => {
    const element = document.querySelector(selector);
    if (!element) return;
    
    if (isLocking) {
      if (!element.dataset.imageGenLocked) {
        element.dataset.originalImageGenDisabled = (element.disabled || false).toString();
        element.dataset.imageGenLocked = 'true';
      }
      element.disabled = true;
      // No opacity change - just disable interaction
    } else {
      if (element.dataset.imageGenLocked === 'true') {
        const wasDisabled = element.dataset.originalImageGenDisabled === 'true';
        element.disabled = wasDisabled;
        delete element.dataset.originalImageGenDisabled;
        delete element.dataset.imageGenLocked;
      }
    }
  });
  
  if (isLocking) {
  } else {
  }
}

// Helper function to show loading state in preview areas
export function setPreviewLoadingState(previewId, isLoading) {
  const preview = $(`#${previewId}`);
  if (!preview) return;
  if (isLoading && previewId === CHARACTER_PREVIEW_ID && activeCharacterPreviewMode !== 'slow') {
    parkSlowWaitIfPresent();
  }
  if (isLoading) {
    css(preview, 'backgroundImage', '');
    html(preview, `
      <div style="display: flex; justify-content: center; align-items: center; height: 100%; color: var(--text-color);">
        <div class="three-dots-loader" style="display: flex; gap: 8px;">
          <div class="dot" style="width: 16px; height: 16px; background: var(--pink-primary); border-radius: 50%; animation: bounce 1.4s infinite ease-in-out both;"></div>
          <div class="dot" style="width: 16px; height: 16px; background: var(--pink-primary); border-radius: 50%; animation: bounce 1.4s infinite ease-in-out both; animation-delay: -0.32s;"></div>
          <div class="dot" style="width: 16px; height: 16px; background: var(--pink-primary); border-radius: 50%; animation: bounce 1.4s infinite ease-in-out both; animation-delay: -0.16s;"></div>
        </div>
        <style>
          @keyframes bounce {
            0%, 80%, 100% { 
              transform: scale(0.8);
              opacity: 0.5;
            } 
            40% { 
              transform: scale(1.2);
              opacity: 1;
            }
          }
        </style>
      </div>
    `);
  }
}

// Helper function to update preview (robust for image/video)
function updatePreview(previewId, mediaUrl, isVideo = false) {
  const preview = $(`#${previewId}`);
  if (!preview) return;
  if (!mediaUrl) {
    html(preview, '<div style="color: var(--color-text-muted); text-align: center; padding: 20px;">No preview available</div>');
    return;
  }

  // Detect type from URL if not specified
  const detectedVideo = mediaUrl.toLowerCase().endsWith('.mp4') || mediaUrl.includes('.mp4?') || mediaUrl.toLowerCase().endsWith('.webm') || mediaUrl.includes('.webm?');
  const finalIsVideo = isVideo || detectedVideo;


  const isBanner = String(previewId || '') === 'banner-image-preview' || String(previewId || '').endsWith('banner-preview');
  const sizeStyle = isBanner
    ? 'width: 100%; height: auto; object-fit: contain; border-radius: 8px; display:block;'
    : 'width: 100%; height: 100%; object-fit: contain; border-radius: 8px;';
  html(preview, finalIsVideo
    ? `<video src="${mediaUrl}" style="${sizeStyle}" autoplay loop muted playsinline></video>`
    : `<img src="${mediaUrl}" alt="Generated image" style="${sizeStyle}">`
  );

  // Special logging for character previews on character page
  if (previewId === 'ai-character-prompt-preview') {
  }
}

// Helper function to show error
async function showError(message, previewId = 'ai-character-prompt-preview') {
  const { showError: showUnifiedError } = await import('../utils/UnifiedNotificationSystem.js');
  const preview = document.getElementById(previewId);
  
  showUnifiedError(message, {
    relativeTo: preview,
    position: 'center',
    duration: 0,
    closable: true
  });
  
  console.error(message);
}

async function showPromptSafetyWarning(previewId = 'ai-character-prompt-preview') {
  const { showWarning: showUnifiedWarning } = await import('../utils/UnifiedNotificationSystem.js');
  const preview = document.getElementById(previewId);
  const lang = getCurrentLanguage?.() ?? 'en';
  const t = window.translations?.[lang]?.['create-character'] ?? window.translations?.en?.['create-character'] ?? {};
  const message = t.prompt_safety_blocked_title || 'Prompt flagged by safety checks';
  const hint = t.prompt_safety_blocked_hint || 'Your prompt has been flagged by our third-party providers for safety reasons. If this seems like a mistake, edit your prompt and try again, or switch the character to Private and use slow preview to bypass fast safety checks.';

  showUnifiedWarning(message, {
    hint,
    relativeTo: preview,
    position: 'center',
    duration: 0,
    closable: true,
    replaceExisting: true
  });
}


/**
 * Low-level transport for `${GENERATION_API_URL}/generate`. Always sends a
 * normalized `style` ("anime" | "realism"), defaulting to "anime" when the
 * caller omits it. Returns the raw response plus parsed JSON so callers keep
 * their own success/error handling.
 */
export async function postGenerate(body, { useAuth = false, useRetry = false, useIdempotency = false, label = 'postGenerate' } = {}) {
  const requestId = createGenerationRequestId();
  const requestBody = {
    ...body,
    style: normalizeStyle(body.style ?? 'anime'),
  };
  if (useIdempotency && !requestBody.request_id) requestBody.request_id = requestId;

  const headers = { 'Content-Type': 'application/json' };
  if (useIdempotency) headers['X-Idempotency-Key'] = requestBody.request_id;
  if (useAuth) {
    const token = await getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  const generateEndpoint = `${GENERATION_API_URL}/generate`;
  const doFetch = async () => {
    const networkResponse = await fetch(generateEndpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });
    if (useRetry && networkResponse.status === 429) {
      const previewBody = await networkResponse.clone().json().catch(() => null);
      if (previewBody && PREVIEW_QUOTA_ERRORS.has(previewBody.error)) return networkResponse;
    }
    if (useRetry && isTransientHttpStatus(networkResponse.status, RETRYABLE_IMAGE_STATUS_CODES)) {
      throw createRetryableHttpError(networkResponse, { endpoint: generateEndpoint, method: 'POST' });
    }
    return networkResponse;
  };

  const response = useRetry
    ? await withRetry(
        doFetch,
        {
          method: 'POST',
          endpoint: generateEndpoint,
          allowUnsafeMethods: true,
          retryableStatusCodes: RETRYABLE_IMAGE_STATUS_CODES,
          onRetryEvent: (payload) => handleRetryTelemetry(label, payload),
        },
        { label, endpoint: generateEndpoint, method: 'POST' }
      )
    : await doFetch();

  const responseText = await response.text();
  let data;
  try {
    data = JSON.parse(responseText);
  } catch (e) {
    throw new Error(`Server returned invalid JSON: ${e.message}. Response: ${responseText}`);
  }

  return { response, data };
}

const characterResultStillCurrent = async (previewId, previewMode, fingerprint, sessionKey, generationToken) => {
  if (isGenerationStale(sessionKey, generationToken)) return false;
  if (previewId !== CHARACTER_PREVIEW_ID) return true;
  const latest = await resolveCharacterPreviewFingerprint();
  if (latest !== fingerprint) {
    discardCharacterSessionIfOwned(previewMode, fingerprint);
    if (normalizePreviewMode(previewMode) === 'slow') slowPathStateByPreview.delete(previewId);
    return false;
  }
  return !isGenerationStale(sessionKey, generationToken);
};

function commitPreviewResult(previewId, previewMode, imageUrl, isVideo = false) {
  if (!imageUrl) return;
  if (previewId === CHARACTER_PREVIEW_ID) {
    const mode = normalizePreviewMode(previewMode);
    rememberCharacterSession(mode, { imageUrl, status: 'ready' });
    if (mode === 'slow') discardSlowWaitNode(previewId);
    if (activeCharacterPreviewMode !== mode) return;
  }
  updatePreview(previewId, imageUrl, isVideo);
}

export async function generateImage(prompt, type, previewId, bannerType = 'image', timeOfDay = null, gender = null, previewMode = 'fast', characterVisibility = 'public', style = 'anime', promptRefs = null, userNegativePrompt = null) {
  if (!prompt || !type) {
    throw new Error('Missing required parameters: ' + (!prompt ? 'prompt ' : '') + (!type ? 'type' : ''));
  }
  const nsfwBlock = getBlockedNsfwPromptMessage(prompt);
  if (nsfwBlock) {
    throw new Error(nsfwBlock);
  }
  const unrecognizedBlock = await getBlockedUnrecognizedPromptMessage(prompt, {
    isLinkedBraceToken: (name) => isLinkedBraceToken(promptRefs, name),
  });
  if (unrecognizedBlock) {
    throw new Error(unrecognizedBlock);
  }

  const mode = normalizePreviewMode(previewMode);
  const sessionKey = previewSessionKey(previewId, mode);
  const generationToken = bumpGenerationToken(sessionKey);
  const fingerprint = (mode === 'slow' || previewId === CHARACTER_PREVIEW_ID)
    ? buildSlowPromptFingerprint({ prompt, type, style, gender, characterVisibility, bannerType, timeOfDay })
    : null;

  if (previewId === CHARACTER_PREVIEW_ID) {
    invalidateOtherCharacterSession(previewId, mode, fingerprint);
    rememberCharacterSession(mode, {
      fingerprint,
      status: 'loading',
      imageUrl: null,
      pendingUrl: null,
      outputKey: null,
    });
  }

  const priorSlow = slowPathStateByPreview.get(previewId);
  const canReuseSlowJob = mode === 'slow'
    && fingerprint === priorSlow?.fingerprint
    && priorSlow?.pendingUrl;

  const applySlowPathResult = async (pendingUrl, { outputKey = null } = {}) => {
    const imageUrl = await pollForQueuedImage(pendingUrl, previewId, {
      promptFingerprint: fingerprint,
      outputKey: outputKey || parseOutputKeyFromPendingUrl(pendingUrl),
      style,
      previewMode: mode,
    });
    if (!(await characterResultStillCurrent(previewId, mode, fingerprint, sessionKey, generationToken))) {
      discardSlowWaitNode(previewId);
      return;
    }
    commitPreviewResult(previewId, mode, imageUrl, false);
    return imageUrl;
  };

  try {
    if (canReuseSlowJob) {
      if (previewId === CHARACTER_PREVIEW_ID) {
        rememberCharacterSession(mode, {
          fingerprint,
          status: 'waiting',
          pendingUrl: priorSlow.pendingUrl,
          outputKey: priorSlow.outputKey || null,
          imageUrl: null,
        });
      }
      return await applySlowPathResult(priorSlow.pendingUrl, {
        outputKey: priorSlow.outputKey,
      });
    }

    let negative_prompt = 'bad quality,worst quality,worst detail,sketch,censor, text, watermark,';
    if (type === 'banner' || type === 'background') {
      negative_prompt += ' humans, people,';
    }

    const parameters = {
      negative_prompt,
      width: type === 'character' ? 1024 : type === 'banner' ? 1024 : 1024,
      height: type === 'character' ? 1360 : type === 'banner' ? 1024 : 1360,
    };

    const requestBody = {
      prompt,
      type,
      format: 'webp',
      parameters,
      bannerType,
      time_of_day: timeOfDay,
      gender,
      style,
      preview_mode: previewMode,
      character_visibility: characterVisibility,
      nsfwAllowed: getNsfwEnabled()
    };
    if (promptRefs && typeof promptRefs === 'object') requestBody.promptRefs = promptRefs;
    if (typeof userNegativePrompt === 'string' && userNegativePrompt.trim()) {
      requestBody.user_negative_prompt = userNegativePrompt.trim();
    }
    if (previewMode === 'slow') requestBody.force_slow_preview = true;

    const { response, data } = await postGenerate(requestBody, {
      useAuth: true,
      useRetry: true,
      useIdempotency: true,
      label: 'generateImage',
    });

    if (isGenerationStale(sessionKey, generationToken)) return;

    if (response.status === 402) {
      throw new Error(readPreviewPageText(
        type,
        'preview_insufficient_credits',
        'Not enough sprite credits for a slow preview.'
      ));
    }

    if (response.status === 429 && PREVIEW_QUOTA_ERRORS.has(data?.error)) {
      if (data.quota) updateStoredQuota(data.quota);
      throw new Error(readPreviewPageText(
        type,
        'fast_preview_quota_exhausted',
        'No free fast previews left. They return at midnight UTC. Slow preview can use sprite credits.'
      ));
    }

    if (response.status === 429) {
      const { handleQuotaError } = await import('../utils/quotaErrorHandler.js');
      const errorPayload = data?.code
        ? data
        : { code: 'quota_exceeded', error: data.error || 'Quota exceeded' };
      if (await handleQuotaError(errorPayload, 'image')) throw new Error('QUOTA_HANDLED');
      if (data.quota) updateStoredQuota(data.quota);
      throw new Error(data.error || 'Rate limit exceeded');
    }

    if (!response.ok) {
      if (data.error === 'prompt_rejected') {
        if (previewId === CHARACTER_PREVIEW_ID && activeCharacterPreviewMode !== mode) {
          rememberCharacterSession(mode, { status: 'error' });
          return;
        }
        await showPromptSafetyWarning(previewId);
        const error = new Error('prompt_rejected');
        error.skipUnifiedErrorNotification = true;
        throw error;
      }
      const resolved = (data.details && resolveImageGenError(data.details)) || (data.error && resolveImageGenError(data.error));
      const errorMessage = resolved || `${data.error || 'Server error'}${data.details ? ': ' + data.details : ''}${data.responseText ? ' ' + data.responseText : ''}`;
      console.error('Server error details:', data.details || 'No additional details', 'Response text:', data.responseText || '');
      throw new Error(errorMessage);
    }

    // Update quota state from any successful response
    if (data.quota) updateStoredQuota(data.quota);
    if (data.credits_remaining != null) applyCreditsRemaining(data.credits_remaining);

    // Slow path: image is queued on Salad
    if (data.status === 'queued' && data.pendingUrl) {
      const outputKey = data.output_key || data.outputKey || parseOutputKeyFromPendingUrl(data.pendingUrl);
      slowPathStateByPreview.set(previewId, { fingerprint, pendingUrl: data.pendingUrl, outputKey });
      if (previewId === CHARACTER_PREVIEW_ID) {
        rememberCharacterSession(mode, {
          fingerprint,
          status: 'waiting',
          pendingUrl: data.pendingUrl,
          outputKey,
          imageUrl: null,
        });
      }
      return await applySlowPathResult(data.pendingUrl, { outputKey });
    }

    if (!data.imageUrl) {
      console.error('No imageUrl received in response:', data);
      throw new Error('No image URL received from server');
    }

    if (!(await characterResultStillCurrent(previewId, mode, fingerprint, sessionKey, generationToken))) return;

    const isVideo = bannerType === 'video' || data.imageUrl.toLowerCase().endsWith('.mp4') || data.imageUrl.toLowerCase().endsWith('.webm');

    commitPreviewResult(previewId, mode, data.imageUrl, isVideo);
    return data.imageUrl;

  } catch (error) {
    if (isGenerationStale(sessionKey, generationToken)) return;
    if (previewId === CHARACTER_PREVIEW_ID) {
      rememberCharacterSession(mode, { status: 'error' });
      if (activeCharacterPreviewMode !== mode) {
        console.error('Error generating image:', error);
        return;
      }
    }
    console.error('Error generating image:', error);
    if (error?.skipUnifiedErrorNotification) throw error;
    if (error.message === 'QUOTA_HANDLED') throw error;
    if (error.message.includes('Failed to fetch')) {
      console.error('Possible CORS or network issue. Check Worker CORS headers and network status.');
    }
    showError(error.message, previewId);
    throw error;
  }
}

// Fetch sprite function
export async function fetchSprite(character_id, outfit_id, ma = 0, aa = null, subdir = 'none', previewId, actionId = null) {
  if (!character_id) {
    throw new Error('character_id is required');
  }
  try {
    const parsedActionId = actionId !== null && actionId !== undefined && !Number.isNaN(parseInt(actionId, 10))
      ? parseInt(actionId, 10)
      : getDefaultStandingActionId();
    const data = await fetchCache.fetchCharacterSprite(
      character_id,
      parsedActionId,
      outfit_id || 0,
      'female',
      true,
      false
    );
    if (!data.spriteUrl) throw new Error('No sprite URL received');

    if (previewId) {
      updatePreview(previewId, data.spriteUrl);
    }

    return data.spriteUrl;
  } catch (error) {
    console.error('Error fetching sprite:', error);
    if (previewId) {
      showError(error.message, previewId);
    }
    throw error;
  }
}

// Main generation functions
async function generateBannerImage(prompt) {
  if (!(await isUserAuthenticated())) {
    showLoginRequired();
    return;
  }

  try {
    setLoadingState('generate-banner-btn', true);
    setPreviewLoadingState('banner-image-preview', true);
    const bannerTypeEl = $(`#ai-banner-type`);
    const bannerType = (bannerTypeEl ? val(bannerTypeEl) : '') || 'image';
    await generateImage(prompt, 'banner', 'banner-image-preview', bannerType);
  } catch (error) {
    console.error('Error in generateBannerImage:', error);
  } finally {
    setLoadingState('generate-banner-btn', false);
  }
}

export async function generateCharacterImage(prompt, { previewMode = 'fast', characterVisibility = 'public', requestSerial = null } = {}) {
  if (!(await isUserAuthenticated())) {
    showLoginRequired();
    return;
  }
  if (!isCurrentCharacterPreviewRequest(requestSerial)) return;

  try {
    activateCharacterPreviewMode(previewMode);
    setLoadingState('ai-generate-character-btn', true);
    setPreviewLoadingState('ai-character-prompt-preview', true);
    
    // Combine character prompt with outfit prompt. Gender count tag stays off the field.
    const outfitPromptEl = $(`#ai-outfit-prompt`);
    const outfitPrompt = (outfitPromptEl ? val(outfitPromptEl) : '') || '';
    const appearanceNegative = document.getElementById('ai-appearance-negative')?.value?.trim() || '';
    const outfitNegative = document.getElementById('ai-outfit-negative')?.value?.trim() || '';
    const userNegativePrompt = [appearanceNegative, outfitNegative].filter(Boolean).join(', ');
    const genderEl = $(`#ai-character-gender`);
    const gender = (genderEl ? val(genderEl) : '') || null;
    const appearance = withCharacterGenderTag(prompt, gender);
    const combinedPrompt = outfitPrompt ? `${appearance}, ${outfitPrompt}` : appearance;
    const cleanedPrompt = await cleanCharacterPrompt(combinedPrompt);

    const style = getCharacterFormStyle();
    if (!isCurrentCharacterPreviewRequest(requestSerial)) return;

    const imageUrl = await generateImage(cleanedPrompt, 'character', 'ai-character-prompt-preview', 'image', null, gender, previewMode, characterVisibility, style, null, userNegativePrompt);
    if (!imageUrl) return;
    if (activeCharacterPreviewMode !== normalizePreviewMode(previewMode)) return imageUrl;

    
    const isScenarioSlot = window.location.search.includes('slot=');
    if (isScenarioSlot) {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const slotStr   = urlParams.get('slot');
        const slotNum   = parseInt(slotStr,10);
        if (!isNaN(slotNum) && slotNum>=1 && slotNum<=3) {
          const { storage } = await import('../utils/storage.js');
          const { savePreview, deletePreview, cleanupAndSavePreview } = await import('../utils/previewCache.js');

          let mainDraft = storage.getJSON('scenario_draft_v2') || {};
          if(!mainDraft.__previews) mainDraft.__previews = {};
          if(!mainDraft.__previews.characters) mainDraft.__previews.characters = {};

          let previewData = imageUrl;
          if(imageUrl.startsWith('http')) previewData = await getBase64FromUrl(imageUrl);
          
          // Clean up old preview and store new one
          if (previewData.startsWith('data:') && previewData.length>100_000) {
            const oldPreviewRef = mainDraft.__previews.characters[slotNum];
            const previewId = await cleanupAndSavePreview(previewData, oldPreviewRef);
            previewData = `db:${previewId}`;
          } else {
            // Clean up old preview if it was stored in IndexedDB
            const oldPreviewRef = mainDraft.__previews.characters[slotNum];
            if (oldPreviewRef && oldPreviewRef.startsWith('db:')) {
              const oldId = oldPreviewRef.slice(3);
              await deletePreview(oldId);
            }
          }

          mainDraft.__previews.characters[slotNum] = previewData;
          storage.setJSON('scenario_draft_v2', mainDraft);
        }
      } catch(e){ console.warn('[ImageGen] Failed to update scenario draft with new preview',e); }
    }
    return imageUrl;
  } catch (error) {
    console.error('[ImageGen] ❌ Error in generateCharacterImage:', error);
    if (error.message === 'QUOTA_HANDLED') return;
    throw error;
  } finally {
    setLoadingState('ai-generate-character-btn', false);
  }
}

export const isCharacterSlowPreviewQueued = () => isCharacterPollActive('slow');

export const revealCharacterPreview = async (mode) => {
  const previewMode = normalizePreviewMode(mode);
  const serialAtStart = characterPreviewRequestSerial;
  const fingerprint = await resolveCharacterPreviewFingerprint();
  if (serialAtStart !== characterPreviewRequestSerial) return false;
  if (!fingerprint) return false;

  const session = characterPreviewSessions.get(previewMode);
  if (!session || session.fingerprint !== fingerprint) return false;
  if (session.status !== 'loading' && session.status !== 'waiting' && session.status !== 'ready') return false;

  activateCharacterPreviewMode(previewMode);

  if (session.status === 'ready' && session.imageUrl) {
    if (previewMode === 'fast') parkSlowWaitIfPresent();
    if (previewMode === 'slow') discardSlowWaitNode(CHARACTER_PREVIEW_ID);
    updatePreview(CHARACTER_PREVIEW_ID, session.imageUrl, false);
    return true;
  }

  if (previewMode === 'slow' && (session.status === 'waiting' || session.status === 'loading' || isCharacterPollActive('slow'))) {
    if (mountSlowWait()) return true;
    setPreviewLoadingState(CHARACTER_PREVIEW_ID, true);
    return true;
  }

  if (previewMode === 'fast' && session.status === 'loading') {
    setPreviewLoadingState(CHARACTER_PREVIEW_ID, true);
    return true;
  }

  return false;
};

export const resetCharacterPreviewSessions = () => {
  bumpGenerationToken(previewSessionKey(CHARACTER_PREVIEW_ID, 'fast'));
  bumpGenerationToken(previewSessionKey(CHARACTER_PREVIEW_ID, 'slow'));
  characterPreviewSessions.clear();
  slowPathStateByPreview.delete(CHARACTER_PREVIEW_ID);
  activeCharacterPreviewMode = null;
  discardSlowWaitNode(CHARACTER_PREVIEW_ID);
  document.getElementById(SLOW_PREVIEW_PARK_ID)?.remove();
};

export const startCharacterPreviewGeneration = async ({ previewMode = 'fast', characterVisibility = 'public', requestSerial = null } = {}) => {
  const promptEl = document.getElementById('ai-character-prompt');
  const outfitPromptEl = document.getElementById('ai-outfit-prompt');
  const prompt = promptEl?.value?.trim() || '';
  const outfitPrompt = outfitPromptEl?.value?.trim() || '';

  if (!prompt) {
    showError('Please enter a character prompt', 'ai-character-prompt-preview');
    return;
  }
  if (!outfitPrompt) {
    showError('Please enter an outfit prompt', 'ai-character-prompt-preview');
    return;
  }

  await generateCharacterImage(prompt, { previewMode, characterVisibility, requestSerial });
};

export async function generateBackgroundPreview(prompt, previewId, timeOfDay = null) {
  try {
    setPreviewLoadingState(previewId, true);
    // For backgrounds we pass timeOfDay (day/sunset/night) and no gender.
    await generateImage(prompt, 'background', previewId, 'image', timeOfDay, null);
  } catch (error) {
    console.error('Error in generateBackgroundPreview:', error);
    if (error.message === 'QUOTA_HANDLED') return;
    showError(error.message, previewId);
  } finally {
    // No button-specific loading state, but you can add if needed
  }
}

// Expose globally for dynamic import-less access
if (typeof window !== 'undefined') {
  window.generateBackgroundPreview = generateBackgroundPreview;
}

async function cleanCharacterPrompt(prompt) {
  const unrecognized = new Set();
  if (!getNsfwEnabled()) {
    await loadDanbooruTags();
    collectUnrecognizedPromptTokens(prompt).forEach((token) => {
      unrecognized.add(token.trim().toLowerCase());
    });
  }
  const promptElements = prompt.split(',');
  const cleanedPromptElements = promptElements.map(element => {
    // Replace double backslashes with single backslashes
    const cleanedElement = element.trim().replace(/\\\\/g, '\\');
    return cleanedElement;
  })
    // Strip category-8 child-safety / provider-warning terms
    .filter((element) => {
      if (!element) return false;
      const lower = element.toLowerCase().replace(/_/g, ' ');
      if (DYNAMIC_CATEGORY_8_KEYWORDS.some((keyword) => lower.includes(keyword.toLowerCase()))) return false;
      if (!getNsfwEnabled() && getBlockedNsfwPromptMessage(element)) return false;
      if (unrecognized.has(element.trim().toLowerCase())) return false;
      return true;
    })
    .join(', ');

  return cleanedPromptElements;
}

// Function to load and display background image from CDN
export async function loadBackgroundImage(sceneId, backgroundId, timeOfDay) {
  const previewDiv = document.getElementById(`bg-preview-${sceneId}`);
  if (!previewDiv) {
    console.error(`Preview div for scene ${sceneId} not found`);
    return;
  }

  if (!backgroundId || !timeOfDay) {
    showError('Please select both background and time of day', `bg-preview-${sceneId}`);
    return;
  }

  try {
    const timeSuffix = timeOfDay.split('.')[1];
    const baseId = Math.floor(backgroundId);

    const timeSuffixes = [timeSuffix, '1', '2', '3'].filter((s, i, arr) => arr.indexOf(s) === i);

    for (const suffix of timeSuffixes) {
      const imageUrl = `${CDN_URL}/data/backgrounds_images/${baseId}.${suffix}.webp`;

      const cacheKey = `${baseId}.${suffix}`;
      if (backgroundImageCache.has(cacheKey)) {
        const cachedImage = backgroundImageCache.get(cacheKey);
        updatePreviewWithImage(previewDiv, cachedImage);
        return;
      }

      try {
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = () => {
            backgroundImageCache.set(cacheKey, imageUrl);
            updatePreviewWithImage(previewDiv, imageUrl);
            resolve();
          };
          img.onerror = reject;
          img.src = imageUrl;
        });
        return;
      } catch (error) {
        console.warn(`Failed to load image ${imageUrl}, trying next variation...`);
        continue;
      }
    }

    showError('Failed to load background image. Please try another option.', `bg-preview-${sceneId}`);
    previewDiv.innerHTML = '<span style="color: rgba(255, 255, 255, 0.5); font-style: italic;">Failed to load background</span>';
  } catch (error) {
    showError('Error loading background image', `bg-preview-${sceneId}`);
    console.error('Error loading background image:', error);
  }
}

// Helper function to update preview with image
function updatePreviewWithImage(previewDiv, imageUrl) {
  previewDiv.innerHTML = '';
  previewDiv.style.backgroundImage = `url(${imageUrl})`;
  previewDiv.style.backgroundSize = 'cover';
  previewDiv.style.backgroundPosition = 'center';
  previewDiv.style.border = 'none';
}

// Preload all background variations for a given background ID
export async function preloadBackgroundVariations(backgroundId) {
  const baseId = Math.floor(backgroundId);
  const timeSuffixes = ['1', '2', '3'];

  for (const suffix of timeSuffixes) {
    const imageUrl = `${CDN_URL}/data/backgrounds_images/${baseId}.${suffix}.webp`;
    const cacheKey = `${baseId}.${suffix}`;

    if (!backgroundImageCache.has(cacheKey)) {
      try {
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = () => {
            backgroundImageCache.set(cacheKey, imageUrl);
            resolve();
          };
          img.onerror = () => {
            console.warn(`Failed to preload image ${imageUrl}, skipping...`);
            resolve();
          };
          img.src = imageUrl;
        });
      } catch (error) {
        console.warn(`Failed to preload background image: ${imageUrl}, skipping...`);
      }
    }
  }
}

// Function to handle background generation
export function startBackgroundGeneration(sceneId, timeOfDay) {
  const select = document.getElementById(`generate-bg-${sceneId}`);
  const backgroundId = select?.value;

  if (!backgroundId || !timeOfDay) {
    return;
  }

  setLoadingState(`generate-bg-${sceneId}`, true);
  loadBackgroundImage(sceneId, backgroundId, timeOfDay).finally(() => {
    setLoadingState(`generate-bg-${sceneId}`, false);
  });
}

// DOM ready and event listeners
ready(function () {
  async function fetchOutfitTranslations(lang) {
    if (outfitCache.data && outfitCache.currentLang === lang) {
      return outfitCache.data;
    }

    try {
      const { fetchCache } = await import('../utils/fetchCache.js');
      const data = await fetchCache.fetchOutfits(lang);

      outfitCache.data = data;
      outfitCache.currentLang = lang;
      return data;
    } catch (error) {
      console.error(`Error fetching outfit translations for ${lang}:`, error);
      if (lang !== 'en') return await fetchOutfitTranslations('en');
      return [];
    }
  }

  async function loadOutfits(lang = getCurrentLanguage()) {
    const data = await fetchOutfitTranslations(lang);
    return { outfits: data };
  }

  async function loadCharacterPool() {
    try {
      const { fetchCache } = await import('../utils/fetchCache.js');
      const data = await fetchCache.fetchCharacterRankings();
      const characterPool = Object.values(data).map(char => {
        const detectedGender = normalizeGenderForFilter(
          char.gender ??
          char.gender_text ??
          char.genderLabel ??
          char.gender_label ??
          char.genderText ??
          (char.details && char.details.gender)
        );
        return {
          id: char.id,
          name: char.name,
          image: char.image,
          gender: detectedGender
        };
      });
      const characterGenderMap = new Map();
      characterPool.forEach(char => {
        characterGenderMap.set(String(char.id), char.gender || '');
      });

      const crewMap = [
        { charSelect: '#crew-char-1', roleSelect: '#crew-role-1', preview: 'character-1-preview' },
        { charSelect: '#crew-char-2', roleSelect: '#crew-role-2', preview: 'character-2-preview' },
        { charSelect: '#crew-char-3', roleSelect: '#crew-role-3', preview: 'character-3-preview' },
      ];

      const lang = getCurrentLanguage();
      const { outfits } = await loadOutfits(lang);
      let sanitizedOutfits = dedupeById(outfits);
      window.outfitsData = sanitizedOutfits;

      const getPlaceholderText = (language, key) =>
        dropdownTranslations[language]?.[key] || dropdownTranslations.en[key] || '';

      const ensureSearchDropdown = (selectId, translationKey, language = lang) => {
        if (!selectId) return;
        const selectEl = document.getElementById(selectId);
        if (!selectEl) return;

        const placeholder = getPlaceholderText(language, translationKey);
        const refreshConfig = {
          placeholder,
          searchLabel: placeholder,
          searchPlaceholder: placeholder
        };
        if (searchDropdownInstances[selectId]) {
          searchDropdownInstances[selectId].refresh(refreshConfig);
          return;
        }
        const instance = initSearchDropdown(selectId, refreshConfig);
        if (instance) {
          searchDropdownInstances[selectId] = instance;
        }
      };

      const resolveCharacterGender = (characterId) => {
        if (!characterId) return '';
        return characterGenderMap.get(String(characterId)) || '';
      };

      const computeAvailableOutfits = (characterId) => {
        const gender = resolveCharacterGender(characterId);
        if (!gender || gender === 'all') return sanitizedOutfits;
        return sanitizedOutfits.filter(outfit => doesOutfitMatchGender(outfit, gender));
      };

      const refreshRoleSelectOptions = (roleSelectEl, characterId, language, options = {}) => {
        const { preserveSelection = true } = options;
        const previousValue = preserveSelection ? val(roleSelectEl) : '';
        let availableOutfits = dedupeById(computeAvailableOutfits(characterId));
        availableOutfits = ensureDefaultOutfitFirst(availableOutfits, sanitizedOutfits);
        html(roleSelectEl, '');
        availableOutfits.forEach(outfit => {
          roleSelectEl.insertAdjacentHTML('beforeend', `<option value="${outfit.id}">${outfit.name}</option>`);
        });

        let selectedValue = '';
        if (previousValue && availableOutfits.some(outfit => String(outfit.id) === String(previousValue))) {
          selectedValue = previousValue;
        } else {
          const defaultEntry = availableOutfits.find(outfit => String(outfit.id) === '0');
          selectedValue = defaultEntry ? '0' : (availableOutfits.length ? String(availableOutfits[0].id) : '');
        }

        val(roleSelectEl, selectedValue);
        return { options: availableOutfits, selectedValue };
      };

      const populateSelect = (selectEl, options, placeholderHtml) => {
        html(selectEl, placeholderHtml);
        options.forEach(item => {
          selectEl.insertAdjacentHTML('beforeend', `<option value="${item.id}">${item.name}</option>`);
        });
      };

      crewMap.forEach(({ charSelect, roleSelect }) => {
        const charSelectEl = $(charSelect);
        const roleSelectEl = $(roleSelect);
        if (!charSelectEl || !roleSelectEl) return;

        populateSelect(charSelectEl,
          characterPool,
          `<option value="">${dropdownTranslations[lang]?.select_character || dropdownTranslations.en.select_character}</option>`
        );
        refreshRoleSelectOptions(roleSelectEl, '', lang, { preserveSelection: false });

        const charId = charSelect.replace('#', '');
        const roleId = roleSelect.replace('#', '');
        ensureSearchDropdown(charId, 'select_character', lang);
        ensureSearchDropdown(roleId, 'select_outfit', lang);
      });

      applyTranslations('create', lang);

      crewMap.forEach(({ charSelect, roleSelect, preview }) => {
        const charSelectEl = $(charSelect);
        const roleSelectEl = $(roleSelect);
        if (!charSelectEl || !roleSelectEl) return;

        on(charSelectEl, 'change', async () => {
          const characterId = val(charSelectEl);
          const currentLang = getCurrentLanguage();
          const { selectedValue } = refreshRoleSelectOptions(
            roleSelectEl, characterId, currentLang, { preserveSelection: false }
          );

          const outfitId = selectedValue || '0';
          if (!characterId) {
            const prevDiv = $(`#${preview}`);
            if (prevDiv) { css(prevDiv, 'backgroundImage', ''); html(prevDiv, ''); }
            return;
          }

          try {
            const spriteUrl = await fetchSprite(
              characterId,
              outfitId,
              null,
              null,
              'none',
              preview,
              getDefaultStandingActionId(resolveCharacterGender(characterId))
            );
            const prevDiv = $(`#${preview}`);
            if (prevDiv) { css(prevDiv, 'backgroundImage', ''); html(prevDiv, `<img src="${spriteUrl}" alt="Character preview" style="width: 100%; height: 100%; object-fit: contain;">`); }
          } catch (error) {
            console.error('Failed to fetch sprite:', error);
          }
        });

        on(roleSelectEl, 'change', async () => {
          const characterId = val(charSelectEl);
          const outfitId = val(roleSelectEl) || 0;
          if (characterId) {
            try {
              const spriteUrl = await fetchSprite(
                characterId,
                outfitId,
                null,
                null,
                'none',
                preview,
                getDefaultStandingActionId(resolveCharacterGender(characterId))
              );
              const prevDiv = $(`#${preview}`);
              if (prevDiv) html(prevDiv, `<img src="${spriteUrl}" alt="Character preview" style="width: 100%; height: 100%; object-fit: contain;">`);
            } catch (error) {
              console.error('Failed to fetch sprite:', error);
            }
          }
        });
      });

      document.addEventListener('languageSwitched', async () => {
        const newLang = getCurrentLanguage();
        const { outfits: newOutfits } = await loadOutfits(newLang);
        sanitizedOutfits = dedupeById(newOutfits);
        window.outfitsData = sanitizedOutfits;

        crewMap.forEach(({ charSelect, roleSelect }) => {
          const charSelectEl = $(charSelect);
          const roleSelectEl = $(roleSelect);
          if (!charSelectEl || !roleSelectEl) return;

          const selectedCharId = val(charSelectEl);
          refreshRoleSelectOptions(roleSelectEl, selectedCharId, newLang, { preserveSelection: true });
          populateSelect(charSelectEl, characterPool,
            `<option value="">${dropdownTranslations[newLang]?.select_character || dropdownTranslations.en.select_character}</option>`
          );
          if (selectedCharId) val(charSelectEl, selectedCharId);

          ensureSearchDropdown(charSelect.replace('#', ''), 'select_character', newLang);
          ensureSearchDropdown(roleSelect.replace('#', ''), 'select_outfit', newLang);
        });
        applyTranslations('create', newLang);
      });
    } catch (error) {
      console.error('Error loading character pool:', error);
    }
  }

  const isScenarioPage = window.location.pathname.includes('create-scenario');

  // Only load character pool if NOT on scenario page (avoid double init with scenarioForm.js)
  if (!isScenarioPage) {
    loadCharacterPool();

    // Banner button handler only for non-scenario pages (scenario page has its own handler in scenarioForm.js)
    const generateBannerBtn = $(`#generate-banner-btn`);
    if (generateBannerBtn) {
      off(generateBannerBtn, 'click');
      on(generateBannerBtn, 'click', async () => {
        const bannerPromptEl = $(`#adventure-banner-prompt`);
        const prompt = bannerPromptEl ? val(bannerPromptEl) : '';
        if (!prompt) { showError('Please enter a banner prompt', 'banner-image-preview'); return; }
        await generateBannerImage(prompt);
      });
    }
  }

  const generateCharBtn = $(`#ai-generate-character-btn`);
  if (generateCharBtn) on(generateCharBtn, 'click', async () => {
    const promptEl = $(`#ai-character-prompt`);
    const outfitPromptEl2 = $(`#ai-outfit-prompt`);
    const prompt = promptEl ? val(promptEl) : '';
    const outfitPrompt = outfitPromptEl2 ? val(outfitPromptEl2) : '';
    if (!prompt) {
      showError('Please enter a character prompt', 'ai-character-prompt-preview');
      return;
    }
    if (!outfitPrompt) {
      showError('Please enter an outfit prompt', 'ai-character-prompt-preview');
      return;
    }
    const previewMode = generateCharBtn.dataset.previewMode === 'slow' ? 'slow' : 'fast';
    const characterVisibility = generateCharBtn.dataset.characterVisibility === 'private' ? 'private' : 'public';
    generateCharBtn.dataset.forceSlowPreview = '0';
    generateCharBtn.dataset.previewMode = 'fast';
    generateCharBtn.dataset.characterVisibility = 'public';
    await generateCharacterImage(prompt, { previewMode, characterVisibility });
  });

  const backgroundSelect = document.getElementById('generate-bg-1');
  if (backgroundSelect) {
    backgroundSelect.addEventListener('change', () => {
      if (backgroundSelect.value) startBackgroundGeneration(1);
    });
  }
  const generateButton = document.getElementById('generate-bg-btn-1');
  if (generateButton) {
    generateButton.addEventListener('click', () => startBackgroundGeneration(1));
  }
});
